/* v4: optimized MP4 first; no deliberate wait for the original MOV.
   Reuse the same video element for mobile playback permission continuity. */
if (!customElements.get('stryde-pump-tips-v4')) {
  customElements.define('stryde-pump-tips-v4', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.details = this.querySelector('details');
      this.poster = this.querySelector('[data-pump-play]');
      this.media = this.querySelector('[data-pump-media]');
      this.status = this.querySelector('[data-pump-status]');
      this.actions = this.querySelector('[data-pump-actions]');
      this.poster.hidden = false;
      this.poster.addEventListener('click', () => this.startVideo(), options);
      this.querySelector('[data-pump-retry]').addEventListener('click', () => {
        this.releaseVideo();
        this.startVideo();
      }, options);
      this.details.addEventListener('toggle', () => {
        if (this.details.open) {
          if (this.actions.hidden) this.prepareVideo();
        } else {
          this.releaseVideo();
          this.setStatus('');
          this.actions.hidden = true;
        }
      }, options);
      if (this.details.open) this.prepareVideo();
    }

    setStatus(message) {
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    clearWatchdog() {
      clearTimeout(this.watchdog);
      this.watchdog = null;
    }

    startWatchdog() {
      if (this.watchdog || !this.started || !this.details.open) return;
      const generation = this.generation;
      // A failure safeguard, not a playback delay: play() runs immediately.
      // Give the lightweight MP4 time on slower networks instead of abandoning it at 3s.
      const delay = 12000;
      this.watchdog = setTimeout(() => {
        this.watchdog = null;
        if (generation === this.generation && this.details.open) this.sourceFailed();
      }, delay);
    }

    sourceFailed() {
      if (!this.video) return;
      this.clearWatchdog();
      if (this.candidate === 0) {
        // No error UI for the primary. Cancel its request and keep the same element.
        this.selectSource(1);
        if (this.started && this.details.open) this.requestPlayback();
      } else if (this.started) {
        const restoreFocus = document.activeElement === this.video;
        this.releaseVideo();
        this.setStatus('The video could not load. Please tap Retry video.');
        this.actions.hidden = false;
        if (restoreFocus) this.querySelector('[data-pump-retry]').focus({ preventScroll: true });
      } else {
        // A preload failure must not show an error before the visitor presses Play.
        this.preloadFailed = true;
      }
    }

    selectSource(candidate) {
      this.clearWatchdog();
      this.candidate = candidate;
      this.generation = (this.generation || 0) + 1;
      this.hasPlayed = false;
      this.preloadFailed = false;
      this.video.src = candidate === 0 ? this.dataset.pumpPrimary : this.dataset.pumpBackup;
      this.video.load();
    }

    prepareVideo() {
      if (this.video) return this.video;
      this.mediaAbort = new AbortController();
      const options = { signal: this.mediaAbort.signal };
      const video = this.querySelector('template').content.querySelector('video').cloneNode(true);
      this.video = video;
      video.controls = false;
      video.tabIndex = -1;
      video.inert = true;
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('loadedmetadata', () => {
        if (this.started) this.fitVideo();
      }, options);
      video.addEventListener('play', () => {
        if (!this.details.open) { video.pause(); return; }
        this.startWatchdog();
      }, options);
      video.addEventListener('playing', () => {
        if (!this.details.open) { video.pause(); return; }
        this.hasPlayed = true;
        this.clearWatchdog();
        this.actions.hidden = true;
        this.setStatus('');
      }, options);
      video.addEventListener('waiting', () => {
        if (!this.started || video.paused || !this.details.open) return;
        this.setStatus(this.hasPlayed ? 'Buffering video…' : 'Loading video…');
        this.startWatchdog();
      }, options);
      video.addEventListener('pause', () => {
        // load() can queue a pause from the previous source; don't cancel a new play request.
        if (video.paused && !video.error) { this.clearWatchdog(); this.setStatus(''); }
      }, options);
      video.addEventListener('ended', () => { this.clearWatchdog(); this.setStatus(''); }, options);
      video.addEventListener('error', () => {
        if (video.error) this.sourceFailed();
      }, options);
      // Select once before attachment: avoid starting the template URL and resetting it.
      this.selectSource(0);
      this.media.append(video);
      return video;
    }

    fitVideo() {
      if (this.video && this.video.videoWidth && this.video.videoHeight) {
        this.media.style.aspectRatio = this.video.videoWidth + '/' + this.video.videoHeight;
      }
    }

    startVideo() {
      if (!this.details.open) return;
      const video = this.prepareVideo();
      // Give an unsuccessful background preload a fresh, user-initiated attempt.
      if (this.preloadFailed) this.selectSource(0);
      this.started = true;
      this.poster.hidden = true;
      this.media.classList.add('is-video');
      this.actions.hidden = true;
      this.fitVideo();
      video.inert = false;
      video.removeAttribute('aria-hidden');
      video.controls = true;
      video.tabIndex = 0;
      video.focus({ preventScroll: true });
      this.requestPlayback();
    }

    requestPlayback() {
      const video = this.video;
      const generation = this.generation;
      this.setStatus('Loading video…');
      this.startWatchdog();
      // The first play() runs synchronously in the click gesture on desktop and mobile.
      const request = video.play();
      if (request) request.catch(error => {
        // Ignore rejections from an abandoned source, closed row or removed section.
        if (!this.isConnected || this.video !== video || generation !== this.generation || !this.details.open) return;
        if (error.name === 'NotAllowedError') {
          // Browser permission is not a broken source; changing files cannot fix it.
          this.clearWatchdog();
          this.setStatus('Tap Play on the video controls to begin.');
        } else if (error.name !== 'AbortError') {
          this.sourceFailed();
        }
      });
    }

    releaseVideo() {
      this.clearWatchdog();
      this.generation = (this.generation || 0) + 1;
      if (this.mediaAbort) this.mediaAbort.abort();
      this.mediaAbort = null;
      const video = this.video;
      this.video = null;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.remove();
      }
      this.started = false;
      if (this.poster) this.poster.hidden = false;
      if (this.media) { this.media.style.aspectRatio = ''; this.media.classList.remove('is-video'); }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.abort = null;
      this.releaseVideo();
      if (this.status) this.setStatus('');
      if (this.actions) this.actions.hidden = true;
    }
  });
}